import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isSubscribedToPush,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  getNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPrefs,
} from '../services/notifications';

function Toggle({ checked, onChange, disabled = false }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed ${checked ? 'bg-primary-green' : 'bg-gray-200'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

const EVENTS = [
  { key: 'Like',          label: 'Likes på opslag',                 desc: 'Når nogen liker dine opslag' },
  { key: 'CommentLike',   label: 'Likes på kommentarer',            desc: 'Når nogen liker dine kommentarer' },
  { key: 'Comment',       label: 'Kommentarer på dine opslag',      desc: 'Når nogen kommenterer dine opslag' },
  { key: 'CoComment',     label: 'Kommentarer på fælles opslag',    desc: 'Når nogen kommenterer et opslag, som du også har kommenteret' },
  { key: 'EventAttendeeComment', label: 'Kommentarer til begivenheder', desc: 'Når nogen kommenterer på et opslag til en begivenhed, du deltager i' },
  { key: 'NewPost',       label: 'Nye opslag',                      desc: 'Når der oprettes et nyt generelt opslag' },
  { key: 'EventInvite',   label: 'Begivenheds-invitationer',        desc: 'Nye begivenheder og invitationer' },
  { key: 'EventReminder', label: 'Påmindelse om tilmelding',        desc: 'Daglig påmindelse når du mangler at svare på en begivenhed' },
  { key: 'Message',       label: 'Beskeder',                        desc: 'Når du modtager en privat besked' },
] as const;

export const NotificationSettingsPage = () => {
  const queryClient = useQueryClient();
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(true);
  const [pushError, setPushError] = useState('');

  const { data: prefs, isLoading: prefsLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: getNotificationPreferences,
  });

  useEffect(() => {
    isSubscribedToPush()
      .then(setIsPushEnabled)
      .catch(() => {})
      .finally(() => setPushLoading(false));
  }, []);

  const subscribeMutation = useMutation({
    mutationFn: subscribeToPushNotifications,
    onSuccess: () => { setIsPushEnabled(true); setPushError(''); },
    onError: (err) => setPushError('Kunne ikke aktivere push: ' + String(err)),
  });

  const unsubscribeMutation = useMutation({
    mutationFn: unsubscribeFromPushNotifications,
    onSuccess: () => { setIsPushEnabled(false); setPushError(''); },
    onError: (err) => setPushError('Kunne ikke deaktivere push: ' + String(err)),
  });

  const prefsMutation = useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences'], data);
    },
  });

  const handleTogglePush = async () => {
    if (isPushEnabled) {
      unsubscribeMutation.mutate();
    } else {
      if (Notification.permission === 'denied') {
        setPushError('Push-notifikationer er blokeret i din browser. Tillad dem i browserindstillingerne.');
        return;
      }
      if (Notification.permission === 'default') {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') { setPushError('Du skal give tilladelse til notifikationer.'); return; }
      }
      subscribeMutation.mutate();
    }
  };

  const handlePrefChange = (key: keyof NotificationPrefs, value: boolean) => {
    prefsMutation.mutate({ [key]: value });
  };

  if (prefsLoading || pushLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-green"></div>
        </div>
      </div>
    );
  }

  const p = prefs!;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h1 className="text-2xl font-bold">Notifikationsindstillinger</h1>
          <p className="text-gray-600 mt-1 text-sm">Vælg præcis hvilke notifikationer du vil modtage og via hvilke kanaler.</p>
        </div>

        {/* Push master toggle */}
        <div className="p-6 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Push-notifikationer</h3>
              <p className="text-gray-600 text-sm mt-1">Modtag notifikationer på din enhed i browseren.</p>
              {Notification.permission === 'denied' && (
                <p className="text-red-600 text-sm mt-2">⚠️ Blokeret i browseren — tillad i browserindstillingerne.</p>
              )}
            </div>
            <Toggle
              checked={isPushEnabled}
              onChange={handleTogglePush}
              disabled={subscribeMutation.isPending || unsubscribeMutation.isPending || !('Notification' in window)}
            />
          </div>
          {pushError && <p className="text-sm text-red-600 mt-2">{pushError}</p>}
        </div>

        {/* Granular table */}
        <div className="p-6">
          <h3 className="font-semibold text-base mb-4">Detaljerede indstillinger</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-3 pr-4 font-medium w-1/2">Begivenhed</th>
                  <th className="pb-3 px-4 font-medium text-center">I appen</th>
                  <th className="pb-3 px-4 font-medium text-center">Push</th>
                  <th className="pb-3 pl-4 font-medium text-center">Email</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {EVENTS.map(({ key, label, desc }) => {
                  const inAppKey = `inApp${key}` as keyof NotificationPrefs;
                  const pushKey = `push${key}` as keyof NotificationPrefs;
                  const emailKey = `email${key}` as keyof NotificationPrefs;
                  return (
                    <tr key={key}>
                      <td className="py-4 pr-4">
                        <div className="font-medium text-gray-900">{label}</div>
                        <div className="text-xs text-gray-500">{desc}</div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Toggle checked={p[inAppKey]} onChange={(v) => handlePrefChange(inAppKey, v)} />
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Toggle
                          checked={p[pushKey] && isPushEnabled}
                          onChange={(v) => handlePrefChange(pushKey, v)}
                          disabled={!isPushEnabled}
                        />
                      </td>
                      <td className="py-4 pl-4 text-center">
                        <Toggle checked={p[emailKey]} onChange={(v) => handlePrefChange(emailKey, v)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!isPushEnabled && (
            <p className="text-xs text-gray-400 mt-3">Push-kolonnen er deaktiveret fordi push-notifikationer er slået fra ovenfor.</p>
          )}
          {prefsMutation.isPending && (
            <p className="text-xs text-primary-green mt-2">Gemmer...</p>
          )}
        </div>
      </div>
    </div>
  );
};
